
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ArrowRight, RefreshCcw, Loader2 } from "lucide-react";
import { useAuth, SubscriptionPlan } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";

const PaymentSuccessPage = () => {
  const { checkSubscription, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  
  // Extract plan and billing period from URL params
  const searchParams = new URLSearchParams(location.search);
  const plan = searchParams.get('plan') as SubscriptionPlan | null;
  const billingPeriod = searchParams.get('billing');
  
  // Plan display names
  const planNames = {
    basic: "Básico (Mensal)",
    premium: "Premium (Trimestral)",
    vip: "VIP (Anual)"
  };
  
  // Billing period display names
  const billingNames = {
    monthly: "Mensal",
    quarterly: "Trimestral",
    annual: "Anual"
  };
  
  // Verify subscription on page load with increased attempts
  useEffect(() => {
    const verifySubscription = async () => {
      try {
        console.log(`Tentativa ${verificationAttempts + 1} de verificação de assinatura`);
        await checkSubscription();
        
        // Aguardar um pouco para verificar se o status foi atualizado
        setTimeout(() => {
          if (user?.plan && user.plan !== 'free') {
            toast({
              title: "Assinatura verificada",
              description: "Sua assinatura foi atualizada com sucesso.",
            });
            setIsVerifying(false);
          } else if (verificationAttempts < 8) {
            // Tentar novamente se ainda não atualizou
            setVerificationAttempts(prev => prev + 1);
          } else {
            setIsVerifying(false);
            toast({
              title: "Verificação incompleta",
              description: "Não foi possível verificar a assinatura automaticamente. Use o botão para atualizar manualmente.",
              variant: "destructive"
            });
          }
        }, 2000);
        
      } catch (error) {
        console.error("Erro ao verificar assinatura:", error);
        // If less than 8 attempts, try again after a delay
        if (verificationAttempts < 8) {
          setTimeout(() => {
            setVerificationAttempts(prev => prev + 1);
          }, 3000); // Try every 3 seconds, up to 8 times
        } else {
          setIsVerifying(false);
          toast({
            title: "Verificação incompleta",
            description: "Não foi possível verificar a assinatura automaticamente. Use o botão para atualizar manualmente.",
            variant: "destructive"
          });
        }
      }
    };
    
    if (isVerifying && verificationAttempts < 8) {
      const timeoutId = setTimeout(verifySubscription, verificationAttempts === 0 ? 1000 : 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [checkSubscription, toast, isVerifying, verificationAttempts, user?.plan]);
  
  const handleRefreshSubscription = async () => {
    setIsVerifying(true);
    setVerificationAttempts(0);
    try {
      await checkSubscription();
      toast({
        title: "Assinatura atualizada",
        description: "As informações da sua assinatura foram atualizadas."
      });
      setIsVerifying(false);
    } catch (error) {
      console.error("Erro ao atualizar assinatura:", error);
      toast({
        title: "Erro ao atualizar assinatura",
        description: "Não foi possível atualizar as informações da sua assinatura.",
        variant: "destructive"
      });
      setIsVerifying(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-24 max-w-3xl">
      <Card className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-900/50">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-green-100 p-4 dark:bg-green-900/50">
              {isVerifying ? (
                <Loader2 className="h-12 w-12 text-green-600 dark:text-green-400 animate-spin" />
              ) : (
                <Check className="h-12 w-12 text-green-600 dark:text-green-400" />
              )}
            </div>
          </div>
          <CardTitle className="text-3xl">Pagamento Concluído</CardTitle>
          <CardDescription className="text-base mt-2">
            {isVerifying 
              ? `Verificando status da assinatura... (Tentativa ${verificationAttempts + 1}/8)` 
              : "Seu pagamento foi processado com sucesso!"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border border-green-200 dark:border-green-800 rounded-lg p-6 bg-white dark:bg-slate-900">
            <h3 className="text-xl font-semibold mb-4">Detalhes da assinatura</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plano:</span>
                <span className="font-medium">
                  {user?.plan && user.plan !== 'free' 
                    ? planNames[user.plan as keyof typeof planNames] || user.plan 
                    : plan ? planNames[plan] || plan : "Carregando..."}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Período:</span>
                <span className="font-medium">
                  {user?.billingPeriod 
                    ? billingNames[user.billingPeriod as keyof typeof billingNames] || user.billingPeriod
                    : billingPeriod ? billingNames[billingPeriod as keyof typeof billingNames] || billingPeriod : "Carregando..."}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <span className={`font-medium ${
                  user?.plan && user.plan !== 'free' ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {isVerifying ? "Verificando..." : 
                   user?.plan && user.plan !== 'free' ? "Ativo" : "Aguardando confirmação"}
                </span>
              </div>
              
              {user?.subscriptionEndDate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Válido até:</span>
                  <span className="font-medium">
                    {new Date(user.subscriptionEndDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>
            
            <div className="mt-6">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full" 
                onClick={handleRefreshSubscription}
                disabled={isVerifying}
              >
                {isVerifying ? (
                  <>
                    <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                    Verificando assinatura...
                  </>
                ) : (
                  <>
                    <RefreshCcw className="h-4 w-4 mr-2" />
                    Atualizar informações da assinatura
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <div className="text-center text-muted-foreground">
            <p>
              {user?.plan && user.plan !== 'free' ? (
                <>Você agora tem acesso a todos os recursos do plano {planNames[user.plan as keyof typeof planNames]}.</>
              ) : (
                <>Aguardando confirmação do pagamento. Se o problema persistir, entre em contato com o suporte.</>
              )}
              {" "}Você pode gerenciar sua assinatura a qualquer momento acessando sua conta.
            </p>
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            className="w-full" 
            onClick={() => navigate("/gerar-jogos")}
            disabled={isVerifying}
          >
            Ir para Gerar Jogos
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
